// src/subscreens/users/Maintain/mechanicChat/MechanicChatPage.jsx

import React, { useState, useRef, useEffect, useContext } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import AppContext from "../../../../context/AppContext.js";

const MechanicChatPage = ({ isRentalAdmin = false }) => {
  const { id } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const { baseUrl, baseUrlForFile } = useContext(AppContext);

  const mechanic = state?.mechanic;

  const [newMessage, setNewMessage] = useState("");
  const [sendLoading, setSendLoading] = useState(false);
  const [messages, setMessages] = useState([]);

  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // ── Guard ────────────────────────────────────────────────────
  if (!mechanic) {
    return (
      <div
        style={{
          padding: "60px",
          textAlign: "center",
          fontFamily: "Poppins, sans-serif",
          minHeight: "60vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <p style={{ color: "#888", marginBottom: "16px" }}>
          Mechanic not found.
        </p>
        <button
          onClick={() => navigate(-1)}
          style={{
            padding: "10px 24px",
            background: "#FDB714",
            border: "none",
            borderRadius: "8px",
            fontWeight: 700,
            cursor: "pointer",
          }}
        >
          Go Back
        </button>
      </div>
    );
  }

  // ── handleSendFirstMessage ───────────────────────────────────
  const handleSendFirstMessage = () => {
    if (!newMessage.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Message Required",
        text: "Please type a message first",
      });
      return;
    }

    const savedCustomer = JSON.parse(localStorage.getItem("LoggedInCustomer"));
    const savedRental = JSON.parse(localStorage.getItem("LoggedInRental"));

    // ── Token — AppContext ke mutabiq sab user types (customer,
    //    rental, mechanic) ka token isi ek key mein save hota hai ──
    const token = localStorage.getItem("authToken");

    if (!savedCustomer && !savedRental) {
      Swal.fire({
        icon: "warning",
        title: "Login Required",
        text: "Please login first",
      });
      navigate("/auth");
      return;
    }

    if (!token) {
      Swal.fire({
        icon: "warning",
        title: "Session Expired",
        text: "Please login again to continue.",
      });
      navigate("/auth");
      return;
    }

    setSendLoading(true);

    const formData = new FormData();
    formData.append("srmechanicadmin_id", mechanic.srmechanicadmin_id);
    formData.append("lastmessage", newMessage);
    formData.append("type", "general");

    let conversationUrl;
    let messageUrl;
    let senderValue;
    let dashboardPath;

    if (savedRental) {
      // ── Rental Admin → Mechanic ──────────────────────────
      formData.append("srrentaladmin_id", savedRental.id);
      conversationUrl = `${baseUrl}/rentaladmin/conversations/createMechanicConversation`;
      messageUrl = `${baseUrl}/rentaladmin/chat/sendMessage`;
      senderValue = "rental";
      dashboardPath = "/rental-admin/messages";
    } else {
      // ── Customer → Mechanic ──────────────────────────────
      formData.append("srcustomer_id", savedCustomer.id);
      conversationUrl = `${baseUrl}/customer/conversations/createMechanicConversation`;
      messageUrl = `${baseUrl}/customer/chat/sendMessage`;
      senderValue = "user";
      dashboardPath = "/user/messages";
    }

    const authHeaders = {
      "Content-Type": "multipart/form-data",
      Authorization: `Bearer ${token}`,
    };

    // ── Step 2: Conversation create / fetch existing ─────────
    axios
      .post(conversationUrl, formData, { headers: authHeaders })
      .then((res) => {
        if (res.data.success !== true) throw new Error(res.data.message);

        const conversationId = res.data.conversation.id;

        // ── Step 3: Message bhejo ────────────────────────
        const msgFormData = new FormData();
        msgFormData.append("srconversation_id", conversationId);
        msgFormData.append("sender", senderValue);
        msgFormData.append("message", newMessage);

        return axios
          .post(messageUrl, msgFormData, { headers: authHeaders })
          .then((msgRes) => {
            if (msgRes.data.success !== true)
              throw new Error(msgRes.data.message);

            setSendLoading(false);
            setNewMessage("");

            // ── Step 4: Messages page pe jao ────────
            navigate(dashboardPath, {
              state: { openConversationId: conversationId },
            });
          });
      })
      .catch((err) => {
        setSendLoading(false);
        console.log("Error:", err);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to send message. Please try again.",
        });
      });
  };

  // ── Helpers ──────────────────────────────────────────────────
  const getInitial = (name) => (name ? name.charAt(0).toUpperCase() : "M");

  const getImageSrc = () => {
    if (!mechanic.image) return null;
    if (mechanic.image.startsWith("http")) return mechanic.image;
    return `${baseUrlForFile}/${mechanic.image}`;
  };

  // ── render ───────────────────────────────────────────────────
  return (
    <>
      <div
        style={{
          background: "#f4f6f8",
          padding: "40px 20px",
          display: "flex",
          justifyContent: "center",
          minHeight: "80vh",
        }}
      >
        <div
          style={{
            background: "#fff",
            borderRadius: "16px",
            width: "100%",
            maxWidth: "860px",
            height: "580px",
            display: "flex",
            overflow: "hidden",
            boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
          }}
        >
          {/* ════ LEFT PANEL ════ */}
          <div
            style={{
              width: "320px",
              flexShrink: 0,
              background: "#2d7a7a",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
              padding: "24px",
            }}
          >
            <span
              style={{
                position: "absolute",
                top: "16px",
                right: "16px",
                background: "#fff",
                color: "#2d7a7a",
                fontSize: "11px",
                fontWeight: 700,
                padding: "4px 10px",
                borderRadius: "20px",
                fontFamily: "Poppins, sans-serif",
                letterSpacing: "0.5px",
              }}
            >
              {mechanic.availability?.toUpperCase() || "AVAILABLE"}
            </span>

            {getImageSrc() ? (
              <img
                src={getImageSrc()}
                alt={mechanic.fullname}
                style={{
                  width: "100%",
                  maxWidth: "240px",
                  height: "300px",
                  objectFit: "cover",
                  borderRadius: "16px",
                  marginBottom: "16px",
                  border: "3px solid rgba(255,255,255,0.3)",
                }}
              />
            ) : (
              <div
                style={{
                  width: "130px",
                  height: "130px",
                  borderRadius: "50%",
                  background: "rgba(255,255,255,0.2)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "48px",
                  fontWeight: 700,
                  color: "#fff",
                  marginBottom: "16px",
                  fontFamily: "Baloo 2, sans-serif",
                }}
              >
                {getInitial(mechanic.fullname)}
              </div>
            )}

            <span
              style={{
                position: "absolute",
                bottom: "16px",
                color: "rgba(255,255,255,0.5)",
                fontSize: "10px",
                fontFamily: "Poppins, sans-serif",
                letterSpacing: "1px",
                textTransform: "uppercase",
              }}
            >
              PROFESSIONAL MECHANIC SERVICE
            </span>
          </div>

          {/* ════ RIGHT PANEL ════ */}
          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            {/* Chat Header */}
            <div
              style={{
                padding: "16px 20px",
                borderBottom: "1px solid #f0f0f0",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexShrink: 0,
              }}
            >
              <div>
                <p
                  style={{
                    fontSize: "15px",
                    fontWeight: 700,
                    fontFamily: "Baloo 2, sans-serif",
                    color: "#111",
                    margin: 0,
                  }}
                >
                  {mechanic.fullname}
                </p>
                <p
                  style={{
                    fontSize: "13px",
                    color: "#888",
                    fontFamily: "Poppins, sans-serif",
                    marginTop: "2px",
                    marginBottom: 0,
                  }}
                >
                  {mechanic.specialization}
                  {mechanic.rate && (
                    <span
                      style={{
                        color: "#FDB714",
                        fontWeight: 600,
                        marginLeft: "8px",
                      }}
                    >
                      Rs. {mechanic.rate}/hr
                    </span>
                  )}
                </p>
              </div>
              <button
                onClick={() => navigate(-1)}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "20px",
                  cursor: "pointer",
                  color: "#aaa",
                  lineHeight: 1,
                }}
              >
                ✕
              </button>
            </div>

            {/* Messages Area */}
            <div
              style={{
                flex: 1,
                overflowY: "auto",
                padding: "20px",
                display: "flex",
                flexDirection: "column",
                gap: "12px",
                background: "#fafafa",
              }}
            >
              <div
                style={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#bbb",
                  fontSize: "14px",
                  fontFamily: "Poppins, sans-serif",
                  textAlign: "center",
                }}
              >
                Send your first message to start a conversation with{" "}
                <strong style={{ marginLeft: "4px" }}>
                  {mechanic.fullname}
                </strong>
              </div>
              <div ref={endRef} />
            </div>

            {/* Input Row */}
            <div
              style={{
                display: "flex",
                padding: "12px 16px",
                gap: "10px",
                borderTop: "1px solid #eee",
                background: "#fff",
                flexShrink: 0,
                alignItems: "center",
              }}
            >
              <input
                style={{
                  flex: 1,
                  padding: "10px 16px",
                  border: "1.5px solid #e5e5e5",
                  borderRadius: "24px",
                  fontSize: "13px",
                  outline: "none",
                  fontFamily: "Poppins, sans-serif",
                  background: "#fafafa",
                  opacity: sendLoading ? 0.6 : 1,
                }}
                placeholder="Type your message..."
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSendFirstMessage();
                  }
                }}
                disabled={sendLoading}
              />
              <button
                onClick={handleSendFirstMessage}
                disabled={sendLoading}
                style={{
                  background: sendLoading ? "#ccc" : "#FDB714",
                  border: "none",
                  borderRadius: "50%",
                  width: "40px",
                  height: "40px",
                  cursor: sendLoading ? "not-allowed" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  transition: "background 0.2s",
                }}
              >
                {sendLoading ? (
                  <span style={{ fontSize: "12px", color: "#fff" }}>...</span>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="#111">
                    <path d="M2 21l21-9L2 3v7l15 2-15 2v7z" />
                  </svg>
                )}
              </button>
            </div>

            {/* Footer */}
            <div
              style={{
                textAlign: "center",
                padding: "8px",
                background: "#fff",
                fontSize: "10px",
                color: "#ccc",
                fontFamily: "Poppins, sans-serif",
                letterSpacing: "1px",
                textTransform: "uppercase",
                flexShrink: 0,
              }}
            >
              PROFESSIONAL AGRICULTURAL EQUIPMENT RENTAL
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default MechanicChatPage;